import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaAward,
} from 'react-icons/fa'
import settingsService from '../services/settingsService'

const Footer = () => {
  const [settings, setSettings] = useState({
    company_name: 'Himachal Yatra',
    company_tagline:
      'Your Himalayan adventure, planned by local experts. 200+ tours across Himachal Pradesh since 2015.',
    copyright_text: 'Himachal Yatra. All rights reserved.',
    contact_address: 'H No 1, Shilon Filling Station, Shimla, Himachal Pradesh, IN 171012',
    contact_phone: '+91 7418100009',
    contact_email: 'info@himachalyatra.com',
    footer_about_text:
      'Shimla-based tour operator with local guides who grew up in the Himalayas. 200+ curated experiences with 24/7 support, flexible booking, and deep local knowledge across Himachal Pradesh.',
    footer_services: ['Tour Packages', 'Hotel Booking', 'Car Rental', 'Tour Guide', 'Activities'],
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    social_youtube: '',
    social_whatsapp: '',
  })

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const response = await settingsService.getPublicSettings()
      if (response.success && response.settings) {
        setSettings((prev) => ({ ...prev, ...response.settings }))
      }
    } catch (error) {
      console.error('Error loading footer settings:', error)
      // Continue with default settings
    }
  }

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return <FaFacebook size={20} />
      case 'twitter':
        return <FaTwitter size={20} />
      case 'instagram':
        return <FaInstagram size={20} />
      case 'linkedin':
        return <FaLinkedin size={20} />
      case 'youtube':
        return <FaYoutube size={20} />
      case 'whatsapp':
        return <FaWhatsapp size={20} />
      default:
        return null
    }
  }

  const socialLinks = [
    { platform: 'facebook', url: settings.social_facebook },
    { platform: 'twitter', url: settings.social_twitter },
    { platform: 'instagram', url: settings.social_instagram },
    { platform: 'linkedin', url: settings.social_linkedin },
    { platform: 'youtube', url: settings.social_youtube },
    {
      platform: 'whatsapp',
      url: settings.social_whatsapp
        ? `https://wa.me/${settings.social_whatsapp.replace(/[^0-9]/g, '')}`
        : '',
    },
  ].filter((link) => link.url && link.url.trim() !== '')

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-display font-bold mb-4">{settings.company_name}</h3>
            <p className="text-gray-400 mb-4">
              {settings.footer_about_text || settings.company_tagline}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex space-x-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition"
                    aria-label={link.platform}
                  >
                    {getSocialIcon(link.platform)}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/tours" className="text-gray-400 hover:text-white transition">
                  Tours
                </Link>
              </li>
              <li>
                <Link
                  to="/services?tab=activity"
                  className="text-gray-400 hover:text-white transition"
                >
                  Adventures
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-white transition">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/tour-planner" className="text-gray-400 hover:text-white transition">
                  Tour Planner
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-and-conditions"
                  className="text-gray-400 hover:text-white transition"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/cancellation-policy"
                  className="text-gray-400 hover:text-white transition"
                >
                  Cancellation Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              {Array.isArray(settings.footer_services) &&
                settings.footer_services.map((service, index) => {
                  // Map service names to routes
                  const getServiceLink = (serviceName) => {
                    const lowerService = serviceName.toLowerCase()
                    if (lowerService.includes('tour package')) return '/tours'
                    if (lowerService.includes('hotel')) return '/services?tab=hotel'
                    if (lowerService.includes('car')) return '/services?tab=car'
                    if (lowerService.includes('guide')) return '/services?tab=guide'
                    if (lowerService.includes('activit')) return '/services?tab=activity'
                    if (lowerService.includes('camp')) return '/services?tab=camp_site'
                    return '/services'
                  }

                  return (
                    <li key={index}>
                      <Link
                        to={getServiceLink(service)}
                        className="text-gray-400 hover:text-white transition"
                      >
                        {service}
                      </Link>
                    </li>
                  )
                })}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              {settings.contact_address && (
                <li className="flex items-start space-x-3">
                  <FaMapMarkerAlt className="text-primary-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-400">{settings.contact_address}</span>
                </li>
              )}
              {settings.contact_phone && (
                <li className="flex items-center space-x-3">
                  <FaPhone className="text-primary-500 flex-shrink-0" />
                  <a
                    href={`tel:${settings.contact_phone}`}
                    className="text-gray-400 hover:text-white transition"
                  >
                    {settings.contact_phone}
                  </a>
                </li>
              )}
              {settings.contact_email && (
                <li className="flex items-center space-x-3">
                  <FaEnvelope className="text-primary-500 flex-shrink-0" />
                  <a
                    href={`mailto:${settings.contact_email}`}
                    className="text-gray-400 hover:text-white transition"
                  >
                    {settings.contact_email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Government Registration Trust Badges */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <p className="text-center text-xs text-gray-500 uppercase tracking-widest mb-4">
            Government Registered &amp; Certified
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/certifications"
              aria-label="HP Tourism Travel Agent Registration — Reg No. 260226/71088. View certificate."
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-800 bg-blue-950/40 text-blue-300 hover:bg-blue-900/50 hover:border-blue-600 transition text-xs font-medium"
            >
              <FaShieldAlt className="flex-shrink-0" size={13} />
              <span>HP Tourism Registered</span>
              <span className="opacity-60 font-normal">No. 260226/71088</span>
            </Link>
            <Link
              to="/certifications"
              aria-label="Udyam MSME Registration — UDYAM-HP-09-0036147. View certificate."
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-green-800 bg-green-950/40 text-green-300 hover:bg-green-900/50 hover:border-green-600 transition text-xs font-medium"
            >
              <FaAward className="flex-shrink-0" size={13} />
              <span>MSME Registered</span>
              <span className="opacity-60 font-normal">UDYAM-HP-09-0036147</span>
            </Link>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-6 pt-6 text-center">
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} {settings.copyright_text}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
